import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import feather from 'feather-icons';

import { useAuth } from './context/AuthContext';

import Navbar from './components/Navbar';
import LoginDrawer from './components/LoginDrawer';
import ProtectedRoute from './components/ProtectedRoute';
import VerifiedRoute from './components/VerifiedRoute';

// Public pages
import HomePage from './pages/HomePage';
import RentalPolicies from './pages/RentalPolicies';
import GettingStarted from './pages/GettingStarted';
import Register from './pages/Register';
import Login from './pages/Login';
import VerifyEmail from './pages/VerifyEmail';
import PrivateRentalsPage from './pages/PrivateRentalsPage';

// Renter pages (auth required)
import Profile from './pages/Profile';
import VerifyIdentity from './pages/VerifyIdentity';
import BookingPage from './pages/BookingPage';
import BookingDetail from './pages/BookingDetail';
import BookingConfirmation from './pages/BookingConfirmation';

function App() {
  const { isLoggedIn, user, loading, login, logout } = useAuth();
  const [isLoginDrawerOpen, setIsLoginDrawerOpen] = useState(false);

  useEffect(() => {
    AOS.init({ duration: 800, easing: 'ease-in-out', once: true });
    feather.replace();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Loading…</p>
        </div>
      </div>
    );
  }

  const openLoginDrawer = () => setIsLoginDrawerOpen(true);
  const closeLoginDrawer = () => setIsLoginDrawerOpen(false);

  const handleLoginSuccess = (_userData) => {
    setIsLoginDrawerOpen(false);
  };

  return (
    <Router>
      <Navbar
        isLoggedIn={isLoggedIn}
        handleLogout={logout}
        userName={user?.profile?.name || user?.profile?.email || 'User'}
        openLoginDrawer={openLoginDrawer}
      />

      <Routes>
        {/* ── Public routes ─────────────────────────────────────────── */}
        <Route path="/"                element={<HomePage />} />
        <Route path="/rental-policies" element={<RentalPolicies />} />
        <Route path="/getting-started" element={<GettingStarted />} />
        <Route path="/register"        element={<Register isOpen={isLoginDrawerOpen} onLoad={closeLoginDrawer} />} />
        <Route path="/login"           element={<Login isOpen={isLoginDrawerOpen} onLoad={closeLoginDrawer} />} />
        <Route path="/verify-email"    element={<VerifyEmail />} />
        <Route path="/private-rentals" element={<VerifiedRoute><PrivateRentalsPage /></VerifiedRoute>} />

        {/* ── Renter routes (auth required) ─────────────────────────── */}
        <Route path="/profile"
          element={<ProtectedRoute><Profile /></ProtectedRoute>}
        />
        <Route path="/verify"
          element={<ProtectedRoute><VerifyIdentity /></ProtectedRoute>}
        />
        <Route path="/book/:vin"
          element={<ProtectedRoute><BookingPage /></ProtectedRoute>}
        />
        <Route path="/booking/:id"
          element={<ProtectedRoute><BookingDetail /></ProtectedRoute>}
        />
        <Route path="/booking/:id/confirmation"
          element={<ProtectedRoute><BookingConfirmation /></ProtectedRoute>}
        />

        {/* ── Fallback ───────────────────────────────────────────────── */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <LoginDrawer
        isOpen={isLoginDrawerOpen}
        onClose={closeLoginDrawer}
        onLoginSuccess={handleLoginSuccess}
      />
    </Router>
  );
}

export default App;
